// Project Overview → "Waiting on client" (Stephanie 2026-10-14): what the rep
// is waiting on and when to chase it. Shows on the rep's dashboard until Done.
// Hidden until the follow-ups table exists (Jake's DB step).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EditDeleteNote } from "@/components/notes/EditDeleteNote";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { shortDate, type FollowUpRow } from "@/components/dashboard/RepFollowUps";

const inDays = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function ProjectFollowUps({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const key = ["project", projectId, "follow-ups"] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<{ available: boolean; follow_ups: FollowUpRow[] }>(`/projects/${projectId}/follow-ups`),
  });
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(inDays(2));
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ["sales", "follow-ups"] });
  };
  const add = useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}/follow-ups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim(), follow_up_on: date }),
      }),
    onSuccess: () => {
      setReason("");
      setError(null);
      refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? "Not saved.") : "Not saved."),
  });
  const done = useMutation({
    mutationFn: (id: string) => api(`/follow-ups/${id}/done`, { method: "POST" }),
    onSuccess: refresh,
  });

  if (!q.data?.available) return null;
  const rows = q.data.follow_ups;
  return (
    <div className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
      <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Waiting on client</h2>
      {rows.length > 0 && (
        <ul className="mt-3">
          {rows.map((f) => (
            <li key={f.id} className="flex items-start justify-between gap-3 border-b border-[#e7e7e4] py-2.5 last:border-0">
              <div className="min-w-0 flex-1">
                {/* Fix the reason or date, or delete one added by mistake —
                    "Done" is for when it's actually done (Stephanie 2026-10-30). */}
                <EditDeleteNote
                  noun="follow-up"
                  fields={[
                    { key: "reason", label: "Reason", value: f.reason },
                    { key: "date", label: "Follow up on", value: f.followUpOn, kind: "date" },
                  ]}
                  onSave={async (v) => {
                    await api(`/follow-ups/${f.id}`, { method: "PATCH", body: JSON.stringify({ reason: (v.reason ?? "").trim(), follow_up_on: v.date }) });
                    refresh();
                  }}
                  onDelete={async () => {
                    await api(`/follow-ups/${f.id}`, { method: "DELETE" });
                    refresh();
                  }}
                >
                  <p className="text-sm text-ink">{f.reason}</p>
                  <p className="text-xs text-muted">
                    Follow up {shortDate(f.followUpOn)}
                    {f.createdBy ? ` · ${f.createdBy}` : ""}
                  </p>
                </EditDeleteNote>
              </div>
              <button
                type="button"
                onClick={() => done.mutate(f.id)}
                disabled={done.isPending}
                className="shrink-0 text-sm font-semibold text-ink hover:text-muted disabled:opacity-50"
              >
                Done
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="mt-3 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim()) add.mutate();
        }}
      >
        <label className="min-w-[220px] flex-1 text-xs text-muted">
          What are you waiting on?
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. Out of stock — client picking a new color"
            className="mt-1 block w-full rounded-md border border-rule px-3 py-2 text-sm text-ink placeholder:text-muted"
          />
        </label>
        <label className="text-xs text-muted">
          Follow up on
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 block rounded-md border border-rule px-3 py-2 text-sm text-ink"
          />
        </label>
        <Button type="submit" size="sm" disabled={!reason.trim() || !date || add.isPending}>
          {add.isPending ? "Saving…" : "Add"}
        </Button>
      </form>
      {error && <p role="alert" className="mt-2 text-xs text-risk">{error}</p>}
    </div>
  );
}
